/**
 * Passkey (WebAuthn) utilities for registration and authentication.
 * Uses @simplewebauthn/browser for browser WebAuthn API interaction.
 */
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

const API = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = window.location.origin;

async function post(path, body, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...body, origin: ORIGIN }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || 'Request failed');
  }
  return r.json();
}

/**
 * Register a new passkey for the currently authenticated user.
 * @param {string} token - JWT auth token
 * @param {string} name - Friendly name for the passkey (e.g. "iPhone Face ID")
 */
export async function registerPasskey(token, name = 'My Passkey') {
  // Step 1: Get registration options from backend
  const options = await post('/api/passkeys/register/options', { name }, token);

  // Step 2: Prompt user for biometric/security key via browser API
  const credential = await startRegistration({ optionsJSON: options });

  // Step 3: Send credential to backend for verification
  return post('/api/passkeys/register/verify', credential, token);
}

/**
 * Authenticate with a passkey (no password needed).
 * Returns { token, user } on success.
 */
export async function loginWithPasskey() {
  // Step 1: Get authentication options from backend
  const options = await post('/api/passkeys/login/options', {});

  // Step 2: Prompt user for biometric/security key
  const assertion = await startAuthentication({ optionsJSON: options });

  // Step 3: Verify assertion and get JWT
  return post('/api/passkeys/login/verify', assertion);
}

/**
 * Check if the browser supports WebAuthn/Passkeys.
 */
export function isPasskeySupported() {
  return !!(window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function');
}

/**
 * Check if the browser supports conditional UI (autofill passkeys).
 */
export async function isConditionalUISupported() {
  if (!isPasskeySupported()) return false;
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable?.() || false;
  } catch {
    return false;
  }
}
